'use client';

import { motion, useScroll, useTransform } from 'framer-motion';
import { Calendar, Dices, Trash2, TrendingDown, UtensilsCrossed } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';

import { Badge } from '@/components/ui/badge';
import { TimelineItem, type TimelineItemData } from '@/features/landing/components/timeline-item';
import { useSectionVisibility } from '@/features/landing/hooks/useSectionVisibility';

const timelineData: TimelineItemData[] = [
  {
    id: 1,
    key: 'item1',
    icon: Calendar,
    color: 'text-slate-500',
    bgColor: 'bg-slate-500/10',
    customImage: '/landing/problem-no-idea.jpeg',
  },
  {
    id: 2,
    key: 'item2',
    icon: Dices,
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
    customImage: '/landing/problem-gambling.jpeg',
  },
  {
    id: 3,
    key: 'item3',
    icon: TrendingDown,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    customImage: '/landing/problem-fortune-teller.jpeg',
  },
  {
    id: 4,
    key: 'item4',
    icon: UtensilsCrossed,
    color: 'text-red-500',
    bgColor: 'bg-red-500/10',
    customImage: '/landing/problem-sold-out.jpeg',
  },
  {
    id: 5,
    key: 'item5',
    icon: Trash2,
    color: 'text-destructive',
    bgColor: 'bg-destructive/10',
    customImage: '/landing/problem-dumpster.jpeg',
  },
];

export function ProblemSection() {
  const t = useTranslations('landing.problem');
  const containerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const { ref: visibilityRef } = useSectionVisibility('problem', { threshold: 0.1 });

  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ['start center', 'end center'],
  });

  const heightTransform = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);
  const opacityTransform = useTransform(scrollYProgress, [0, 0.1], [0, 1]);

  return (
    <div
      id="problem"
      className="w-full bg-muted/30 font-sans md:px-10 py-24"
      ref={(el) => {
        (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        (visibilityRef as React.MutableRefObject<HTMLElement | null>).current = el;
      }}
    >
      {/* Header Section */}
      <div className="max-w-4xl mx-auto text-center mb-20 px-4 md:px-8 lg:px-10">
        <Badge
          variant="outline"
          className="mb-6 px-4 py-1.5 text-sm border-destructive/30 bg-destructive/10 text-destructive"
        >
          {t('badge')}
        </Badge>
        <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6 text-foreground">
          {t('title')}
        </h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          {t.rich('subtitle', {
            danger: (chunks) => <span className="text-destructive font-medium">{chunks}</span>,
          })}
        </p>
      </div>

      {/* Timeline Section */}
      <div ref={timelineRef} className="relative max-w-7xl mx-auto pb-20 px-4">
        {timelineData.map((item, index) => (
          <TimelineItem
            key={item.id}
            item={item}
            index={index}
            translationNamespace="landing.problem"
            variant="problem"
          />
        ))}

        {/* Timeline Line - Mobile (left) and Desktop (center) */}
        <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-[2px] bg-[linear-gradient(to_bottom,var(--tw-gradient-stops))] from-transparent from-[0%] via-border to-transparent to-[99%] [mask-image:linear-gradient(to_bottom,transparent_0%,black_10%,black_90%,transparent_100%)] md:-translate-x-1/2">
          <motion.div
            style={{
              height: heightTransform,
              opacity: opacityTransform,
            }}
            className="absolute inset-x-0 top-0 w-[2px] bg-gradient-to-t from-orange-500 via-red-500 to-transparent from-[0%] via-[10%] rounded-full"
          />
        </div>
      </div>

      {/* Closing Section - The Real Problem */}
      <div className="max-w-4xl mx-auto mt-16 px-4 md:px-8 lg:px-10">
        <div className="p-8 md:p-12 bg-blue-50 dark:bg-blue-950/20 rounded-2xl border border-blue-200 dark:border-blue-900">
          <h3 className="text-2xl md:text-3xl font-bold mb-4 text-foreground">
            {t('realProblem.title')}
          </h3>
          <p className="text-lg text-muted-foreground mb-4 leading-relaxed">
            {t('realProblem.description1')}
          </p>
          <p className="text-lg text-muted-foreground mb-4 leading-relaxed">
            {t.rich('realProblem.description2', {
              danger: (chunks) => <strong className="text-foreground">{chunks}</strong>,
            })}
          </p>
          <p className="text-lg text-muted-foreground mb-2 leading-relaxed">
            {t.rich('realProblem.description3', {
              danger: (chunks) => <strong className="text-foreground">{chunks}</strong>,
            })}
          </p>
          <p className="text-sm italic text-muted-foreground/80 mt-4">{t('realProblem.italic')}</p>
        </div>
      </div>
    </div>
  );
}
